//eslint-disable-next-line
//@ts-nocheck

import {
  CustomIndicator,
  PineJS,
  RawStudyMetaInfoId,
  StudyInputType,
  StudyPlotType,
  StudyTargetPriceScale,
} from "@/lib/datafeed/tvTypes.ts";

import { LineStudyPlotStyle } from "@/lib/datafeed/utils.ts";

export const garchVolatilityEstimation = (PineJS: PineJS): CustomIndicator => {
  return {
    name: "GARCH Volatility Estimation",
    metainfo: {
      _metainfoVersion: 51,
      id: "garch-volatility-estimation@tv-basicstudies-1" as RawStudyMetaInfoId,
      description: "[SFQA] GARCH Volatility Estimation",
      shortDescription: "[SF] GARCH",
      is_hidden_study: false,
      is_price_study: false,
      isCustomIndicator: true,
      linkedToSeries: false,
      priceScale: StudyTargetPriceScale.NoScale,
      format: {
        type: "price",
        precision: 6,
      },
      plots: [
        { id: "garchPlot", type: StudyPlotType.Line },
        { id: "stDevPlot", type: StudyPlotType.Line },
        { id: "atrPlot", type: StudyPlotType.Line },
      ],
      defaults: {
        styles: {
          garchPlot: {
            linestyle: 0,
            visible: true,
            linewidth: 1,
            plottype: LineStudyPlotStyle.StepLine,
            trackPrice: false,
            color: "rgb(255, 255, 255)",
            transparency: 0,
          },
          stDevPlot: {
            linestyle: 0,
            visible: true,
            linewidth: 1,
            plottype: LineStudyPlotStyle.StepLine,
            trackPrice: false,
            color: "rgb(13, 157, 219)",
            transparency: 0,
          },
          atrPlot: {
            linestyle: 0,
            visible: true,
            linewidth: 1,
            plottype: LineStudyPlotStyle.StepLine,
            trackPrice: false,
            color: "rgb(255, 160, 40)",
            transparency: 0,
          },
        },
        inputs: {
          modelInput: "GARCH-M",
          measureTypeInput: "pips",
          omegaInput: 0.000001,
          alphaInput: 0.1,
          betaInput: 0.85,
          deltaInput: 2.0,
          lengthInput: 20,
        },
      },
      styles: {
        garchPlot: {
          title: "GARCH Vol",
          histogramBase: 0,
        },
        stDevPlot: {
          title: "σ",
          histogramBase: 0,
        },
        atrPlot: {
          title: "ATR",
          histogramBase: 0,
        },
      },
      inputs: [
        {
          id: "modelInput",
          name: "Volatility Model",
          defval: "GARCH",
          type: StudyInputType.Text,
          options: ["GARCH", "GARCH-M", "AGARCH", "GJR-GARCH"],
          group: "Calculation Method",
        },
        {
          id: "measureTypeInput",
          name: "Measure",
          defval: "pips",
          type: StudyInputType.Text,
          options: ["pips", "percent"],
          group: "Calculation Method",
        },
        {
          id: "omegaInput",
          name: "Omega",
          defval: 0.000001,
          min: 0.00000001,
          type: StudyInputType.Float,
          group: "GARCH Model Parameters",
        },
        {
          id: "alphaInput",
          name: "Alpha",
          defval: 0.1,
          min: 0.0,
          type: StudyInputType.Float,
          group: "GARCH Model Parameters",
        },
        {
          id: "betaInput",
          name: "Beta",
          defval: 0.85,
          min: 0.0,
          type: StudyInputType.Float,
          group: "GARCH Model Parameters",
        },
        {
          id: "deltaInput",
          name: "Delta",
          defval: 2.0,
          min: 0.01,
          max: 2.0,
          type: StudyInputType.Float,
          group: "GARCH Model Parameters",
        },
        {
          id: "lengthInput",
          name: "Length",
          defval: 20,
          min: 1,
          type: StudyInputType.Integer,
          group: "GARCH Model Parameters",
        },
      ],
    },

    constructor: function () {
      this.init = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;
      };

      this.main = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;

        // User inputs
        const modelInput: string = this._input(0);
        const measureTypeInput: string = this._input(1);
        const omegaInput: number = this._input(2);
        const alphaInput: number = this._input(3);
        const betaInput: number = this._input(4);
        const deltaInput: number = this._input(5);
        const lengthInput: number = this._input(6);

        // Stationarity Check
        if (alphaInput + betaInput >= 1.0) {
          return [0, 0, 0]; // Return zeros if model is non-stationary
        }

        // Market data
        const close: number = PineJS.Std.close(this._context);
        const closeSeries: any = this._context.new_var(close);
        const hlc3: number = PineJS.Std.hlc3(this._context);

        // Return Calculation
        const logReturns: number = PineJS.Std.log(close / closeSeries.get(1));
        const emaPrice: number = PineJS.Std.ema(
          closeSeries,
          lengthInput,
          this._context,
        );
        const returns: number = close - emaPrice;
        const returnsSeries: any = this._context.new_var(returns);
        const negReturns: number = returns < 0 ? returns : 0;
        const negReturnsSeries: any = this._context.new_var(negReturns);

        const stdevRet: number = PineJS.Std.stdev(
          returnsSeries,
          lengthInput,
          this._context,
        );
        const stdevNeg: number = PineJS.Std.stdev(
          negReturnsSeries,
          lengthInput,
          this._context,
        );
        const gamma: number = stdevNeg / stdevRet;
        const shock: number = returnsSeries.get(1) < 0 ? 1.0 : 0.0;

        let volatility: number = 0;

        if (modelInput === "GARCH") {
          const rz2: number = PineJS.Std.pow(logReturns, 2);
          const volatilitySeries: any = this._context.new_var(volatility);
          const cv1: number =
            volatilitySeries.get(1) ||
            omegaInput / (1 - alphaInput - betaInput);
          const varG: number =
            omegaInput +
            alphaInput * rz2 +
            betaInput * PineJS.Std.pow(cv1 * 0.01, 2);

          volatility = PineJS.Std.sqrt(varG) * 100;
        } else {
          let variance: number = 0;
          let newVar: number = 0;
          const condZero: number = 0;

          let newsImpact: number = 0;
          let retSq: number = 0;

          for (let i = 0; i < 100; i++) {
            switch (modelInput) {
              case "AGARCH":
                newsImpact = close < emaPrice ? gamma : 0;
                break;
              case "GJR-GARCH":
                newsImpact = gamma * shock;
                break;
              default:
                newsImpact = 0;
            }

            retSq = PineJS.Std.pow(returns, 2);
            newVar =
              (alphaInput + newsImpact) * retSq +
              betaInput * (variance || condZero);

            if (variance && PineJS.Std.abs(newVar - variance) < omegaInput) {
              break;
            }

            variance = newVar;
          }

          volatility = (PineJS.Std.sqrt(variance) / emaPrice) * 100;

          if (modelInput === "GARCH-M") {
            volatility = (100 + volatility) * 0.01 * volatility;
          }
        }

        let stDev: number = PineJS.Std.stdev(
          closeSeries,
          lengthInput,
          this._context,
        );
        let atr: number = PineJS.Std.atr(lengthInput, this._context);
        let garch: number = volatility;

        // Normalization
        if (measureTypeInput === "percent") {
          stDev /= hlc3 * 0.01;
          atr /= hlc3 * 0.01;
        } else {
          garch *= hlc3 * 0.01;
        }

        return [garch, stDev, atr];
      };
    },
  };
};
